import { LoadingButton } from '@mui/lab'
import {
  Card,
  CardContent,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { GreenText, LabelField } from 'components'
import { saveReadyToSendVehicles } from 'controllers/conformity/ConformityController'
import { ConformityListDto, ReadyToSendDto, ReadyToSendVehicleDto } from 'dtos/conformity'
import { ConformityListContext } from 'enums/conformity/ConformityListContext'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { clientSidePagination, formatDateMoment } from 'utils'
import ConformityZipDownloadLink from './ConformityZipDownloadLink'
import ConformityZipPreviewLink from './ConformityZipPreviewLink'
import DownloadSummarySheetLink from './DownloadSummarySheetLink'

interface ReadyToSendCardProps {
  readyToSend: ReadyToSendDto[]
  refreshLists: (conformityListDto: ConformityListDto) => void
}

export default function ReadyToSendCard({ readyToSend, refreshLists }: ReadyToSendCardProps) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isSavingAndMarkingAsSentToDOT, setIsSavingAndMarkingAsSentToDOT] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const [selected, setSelected] = useState<ReadyToSendVehicleDto[]>([])

  useEffect(() => {
    setIsSubmitting(isSaving || isSavingAndMarkingAsSentToDOT)
  }, [isSaving, isSavingAndMarkingAsSentToDOT])

  useEffect(() => {
    setPage(0)
  }, [pageSize])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Ready To Send
            </Typography>
          </Grid>

          {readyToSend.slice(...clientSidePagination(page, pageSize)).map(inboundTrip => (
            <Grid item xs={12}>
              <Card variant='outlined'>
                <CardContent>
                  <Grid container spacing={1} alignItems='flex-start'>
                    <Grid item xs={12} sm={3} container spacing={1} alignItems='flex-start'>
                      <Grid item xs={12}>
                        <LabelField label='Entry Date' value={inboundTrip.entryDate ? formatDateMoment(inboundTrip.entryDate) : undefined} />
                      </Grid>

                      <Grid item xs={12}>
                        <LabelField label='Entry #' value={inboundTrip.entryNumber} />
                      </Grid>

                      <Grid item xs={12}>
                        <LabelField label='Port of Entry' value={inboundTrip.portOfEntry} />
                      </Grid>

                      <Grid item xs={12}>
                        <LabelField label='Port Code' value={inboundTrip.portCode} />
                      </Grid>

                      <Grid item xs={12}>
                        <DownloadSummarySheetLink
                          context={ConformityListContext.ReadyToSend}
                          disabled={isSubmitting}
                          entryNumber={inboundTrip.entryNumber}
                          inboundTripGuid={inboundTrip.inboundTripGuid}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={9}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell padding='checkbox'></TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Importer Stock #</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Exporter</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>PAPS #</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>VIN</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Year</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Make</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Model</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>DOT Rejection Date</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Conformity Zip Preview</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Conformity Zip Download</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Conformity Package Tracking #</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {inboundTrip.vehicles.map((vehicle, index) => (
                              <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                                <TableCell padding='checkbox'>
                                  <Checkbox
                                    checked={selected.some(value => value.vehicleGuid === vehicle.vehicleGuid)}
                                    disabled={isSubmitting}
                                    onChange={() => {
                                      if (selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)) {
                                        setSelected(selected.filter(selected => selected.vehicleGuid !== vehicle.vehicleGuid))
                                      } else {
                                        setSelected([...selected, vehicle])
                                      }
                                    }}
                                  />
                                </TableCell>

                                <TableCell>{vehicle.importerStockNumber}</TableCell>

                                <TableCell>{vehicle.exporterCompany}</TableCell>

                                <TableCell>{vehicle.papsNumber}</TableCell>

                                <TableCell>{vehicle.vin}</TableCell>

                                <TableCell>{vehicle.year}</TableCell>

                                <TableCell>{vehicle.make}</TableCell>

                                <TableCell>{vehicle.model}</TableCell>

                                <TableCell>{vehicle.dotRejectionDate ? formatDateMoment(vehicle.dotRejectionDate) : '-'}</TableCell>

                                <TableCell>
                                  <ConformityZipPreviewLink disabled={isSubmitting} vehicleGuid={vehicle.vehicleGuid} />
                                </TableCell>

                                <TableCell>
                                  <ConformityZipDownloadLink disabled={isSubmitting} vehicleGuid={vehicle.vehicleGuid} vin={vehicle.vin} />
                                </TableCell>

                                <TableCell>
                                  <TextField
                                    disabled={isSubmitting || !selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)}
                                    value={
                                      selected.some(selected => selected.vehicleGuid === vehicle.vehicleGuid)
                                        ? selected.filter(selected => selected.vehicleGuid === vehicle.vehicleGuid)[0].conformityPackageTrackingNumber
                                        : ''
                                    }
                                    onChange={e => {
                                      setSelected(
                                        selected.map(selected =>
                                          selected.vehicleGuid === vehicle.vehicleGuid
                                            ? { ...selected, conformityPackageTrackingNumber: e.target.value }
                                            : selected
                                        )
                                      )
                                    }}
                                    size='small'
                                    margin='none'
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12} alignItems='center' justifyItems='flex-end'>
            <TablePagination
              count={readyToSend.length ?? 0}
              labelRowsPerPage='Cards per page:'
              onPageChange={(_, page) => setPage(page)}
              onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              SelectProps={{ fullWidth: false }}
              sx={{ borderBottom: 'none' }}
            />
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Ready To Send Table:**

Vehicles appear in the Ready To Send table when they meet the following conditions:
   - A Signed Statement of Conformity is associated with them under Vehicle Details – Documents.
   - They do not have a Conformity Package Tracking # and a Sent to DOT date under Vehicle Details – Conformity.

Vehicles are grouped by their Entry #, which is retrieved from the Inbound Trip the vehicle is associated with. The following details are also pulled from the Inbound Trip:
   - Entry Date
   - Entry #
   - Port of Entry
   - Port Code

**Download Summary Sheet Button:**

Clicking Download Summary Sheet within a Conformity Vehicle Group downloads an Excel file named:
   - Conformity Summary Sheet {Entry #}.xlsx

This file contains a row for each vehicle within the group.

**Conformity Zip Preview Column:**

Clicking “Preview” in a vehicle's row opens a browser tab or pop-up displaying each document and photo included in that vehicle’s Conformity Zip.

**Conformity Zip Download Column:**

Clicking “Download” in a vehicle’s row downloads that vehicle’s Conformity Zip to the user’s machine.

**Conformity Package Tracking # Column:**

A free-text field where users can enter the Conformity Package Tracking # for each vehicle.

**Save Button:**

Clicking Save stores the entered Conformity Package Tracking # for each vehicle where a tracking number has been provided.

**Save and Mark Selected As Sent To DOT:**

Clicking Save and Mark Selected As Sent to DOT triggers a validation check:
   - Ensures that all selected vehicles have a Conformity Package Tracking # entered.

If any selected vehicle is missing this information:
   - The corresponding field turns red.
   - A warning message is displayed: “Required field”.

If all selected vehicles have a Conformity Package Tracking #, the following updates occur:
   - The entered Conformity Package Tracking # is saved.
   - The Sent to DOT date is recorded in Vehicle Details – Conformity (tab).`}
            </GreenText>
          </Grid>

          <Grid item container alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                disabled={selected.length === 0 || isSubmitting}
                loading={isSaving}
                onClick={() => {
                  setIsSaving(true)
                  saveReadyToSendVehicles(selected, false)
                    .then(res => {
                      enqueueSnackbar('Vehicles Saved Successfully!', { variant: 'success' })
                      refreshLists(res.value)
                    })
                    .finally(() => {
                      setIsSaving(false)
                    })
                }}
                variant='contained'
              >
                SAVE
              </LoadingButton>
            </Grid>

            <Grid item>
              <LoadingButton
                disabled={selected.length === 0 || isSubmitting}
                loading={isSavingAndMarkingAsSentToDOT}
                onClick={() => {
                  setIsSavingAndMarkingAsSentToDOT(true)
                  saveReadyToSendVehicles(selected, true)
                    .then(res => {
                      enqueueSnackbar('Vehicles Saved and Marked as Sent To DOT Successfully!', { variant: 'success' })
                      refreshLists(res.value)
                      setSelected([])
                    })
                    .finally(() => {
                      setIsSavingAndMarkingAsSentToDOT(false)
                    })
                }}
                variant='contained'
              >
                SAVE AND MARK SELECTED AS SENT TO DOT
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
